import { PickupAnswer, pickupLocationType } from "../..";

type ContactInformationInput = {
  names: {
    givenName: string;
    familyName: string;
  }[];
  email: string | null;
  confirmEmail: string | null;
  contactNumber: string | null;
  acceptsMarketing: boolean;
};

export type ValidationError = {
  invalidArgs: {
    [key: string]: string;
  };
};

type ContactInformation = {
  names: {
    givenName: string;
    familyName: string;
  }[];
  email: string;
  confirmEmail: string;
  contactNumber?: string;
  acceptsMarketing: boolean;
};

type ValidationFailure = [ValidationError, null];

type ValidationSuccess<T> = [null, T];

export type ValidationResult<T> = ValidationFailure | ValidationSuccess<T>;

function hasErrors(result: ValidationError) {
  return Object.keys(result.invalidArgs).length > 0;
}

function isNullOrUndefined(x: any | null | undefined) {
  return x == null;
}

function isEmptyString(str: string | null | undefined) {
  return isNullOrUndefined(str) || str === "";
}

export function isEmail(str: string) {
  let re =
    /^(([^<>()\[\]\\.,;:\s@“]+(\.[^<>()\[\]\\.,;:\s@“]+)*)|(“.+“))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/; // eslint-disable-line
  return re.test(String(str).toLowerCase());
}

export function validateContactInformationInput(
  input: ContactInformationInput
): ValidationResult<ContactInformation> {
  const validationError: ValidationError = { invalidArgs: {} };

  input.names.some(({ givenName, familyName }) => {
    if (isEmptyString(givenName)) {
      validationError.invalidArgs.givenName = "First name is missing";
    }

    if (isEmptyString(familyName)) {
      validationError.invalidArgs.familyName = "Last name is missing";
    }
  });

  if (isEmptyString(input.email)) {
    validationError.invalidArgs.email = "Email is missing";
  } else {
    if (!isEmail(input.email as string)) {
      validationError.invalidArgs.email = "The email format is invalid";
    }
  }

  if (input.email !== input.confirmEmail) {
    validationError.invalidArgs.confirmEmail = "Emails do not match";
  }
  if (hasErrors(validationError)) {
    return [validationError, null];
  }

  return [null, input as ContactInformation];
}

type ReservationDateTimeInput = {
  reservationDate: Date | null;
  reservationTime: string | null;
};

type ReservationDateTime = {
  reservationDate: Date;
  reservationTime: string;
};

export function validateReservationDateTimeInput(
  input: ReservationDateTimeInput
): ValidationResult<ReservationDateTime> {
  const validationError: ValidationError = { invalidArgs: {} };

  if (
    isNullOrUndefined(input.reservationDate) ||
    isNullOrUndefined(input.reservationTime)
  ) {
    validationError.invalidArgs.reservationDate =
      "Reservation Date & Time is missing";
  } else {
    ("Reservation Date & Time is invalid");
  }

  if (hasErrors(validationError)) {
    return [validationError, null];
  }

  return [null, input as ReservationDateTime];
}

type PartyInput = {
  isPrivate?: boolean;
  minPrivateGroupSize?: number;
  maxPrivateGroupSize?: number;
  details: Array<{
    typeId: string;
    customerType: string;
    price: number;
    quantity: number;
    excludeFromPrivateGroupCount: boolean;
  }>;
};

type OrderDetailItem = {
  typeId: string;
  customerType?: string;
  price: number;
  quantity: number;
};

type Party = {
  details: Array<OrderDetailItem>;
  isPrivate?: boolean;
};

function hasMinimum(details: Array<OrderDetailItem>, minimum = 1) {
  if (details.length === 0) {
    return false;
  }

  return details.reduce((acc, next) => acc + next.quantity, 0) >= minimum;
}

export function validatePartyInput(input: PartyInput): ValidationResult<Party> {
  const validationError: ValidationError = { invalidArgs: {} };

  if (!hasMinimum(input.details))
    validationError.invalidArgs.details = "Number of guests is missing";
  else if (input.isPrivate) {
    const groupSize = input.details
      .filter((detail) => !detail.excludeFromPrivateGroupCount)
      .reduce((sum, { quantity }) => sum + quantity, 0);
    if (groupSize < input.minPrivateGroupSize)
      validationError.invalidArgs.details = `Require at least ${input.minPrivateGroupSize} guests for a private tour`;
    else if (groupSize > input.maxPrivateGroupSize)
      validationError.invalidArgs.details = `Private tours can only accommodate a maximum of ${input.maxPrivateGroupSize} guests`;
  }

  if (hasErrors(validationError)) {
    return [validationError, null];
  }

  return [null, input as Party];
}

export function validatePickupLocation(
  pickupAnswer: PickupAnswer,
  pickupLocation: pickupLocationType | undefined
): ValidationResult<null> {
  const validationError: ValidationError = { invalidArgs: {} };

  if (pickupAnswer === PickupAnswer.IKNOW && !pickupLocation)
    validationError.invalidArgs.details = "Please confirm your pickup location";

  if (hasErrors(validationError)) {
    return [validationError, null];
  }

  return [null, null];
}

type OptionFormInput = {
  couponCode: string | null;
  specialRequests: string | null;
  guideId: string | null;
};

type PickupInput = {
  pickupAnswer: PickupAnswer;
  pickupLocation: pickupLocationType | undefined;
};

export type FormStateInput = PartyInput &
  ReservationDateTimeInput &
  ContactInformationInput &
  OptionFormInput;

export type FormState = Party &
  ReservationDateTime &
  ContactInformation &
  OptionFormInput;

export function validateFormInput(
  formInput: FormStateInput & PickupInput
): ValidationResult<FormState> {
  const [contactInformationErrors, contactInformation] =
    validateContactInformationInput(formInput);

  const [reservationDateTimeErrors, reservationDateTime] =
    validateReservationDateTimeInput(formInput);

  const [partyErrors, party] = validatePartyInput(formInput);

  const [pickupLocationErrors] = validatePickupLocation(
    formInput.pickupAnswer,
    formInput.pickupLocation
  );

  if (
    partyErrors ||
    contactInformationErrors ||
    reservationDateTimeErrors ||
    pickupLocationErrors
  ) {
    const validationErrors: ValidationError = {
      invalidArgs: {
        ...(partyErrors && partyErrors.invalidArgs),
        ...(contactInformationErrors && contactInformationErrors.invalidArgs),
        ...(reservationDateTimeErrors && reservationDateTimeErrors.invalidArgs),
        ...(pickupLocationErrors && pickupLocationErrors.invalidArgs),
      },
    };

    return [validationErrors, null];
  }

  const result = {
    ...party,
    ...reservationDateTime,
    ...contactInformation,
  };

  return [null, result as FormState];
}
